import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pf_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("pf_token");
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    // Ensure no stale Authorization header is attached to the login request.
    try { localStorage.removeItem("pf_token"); } catch (_) { /* ignore */ }
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pf_token", data.token);
    setUser(data.user);
  }, []);

  const signup = useCallback(async (email, password, name) => {
    // Ensure no stale Authorization header is attached to the signup request.
    try { localStorage.removeItem("pf_token"); } catch (_) { /* ignore */ }
    const { data } = await api.post("/auth/signup", { email, password, name });
    // New backend: signup returns { verification_required, user } and NO
    // token — the user must verify email before receiving a usable JWT.
    // Legacy backend behavior (with token) is still handled for safety.
    if (data?.token) {
      localStorage.setItem("pf_token", data.token);
      setUser(data.user);
    }
    return data;
  }, []);

  const loginGoogle = useCallback(async (session_id) => {
    try { localStorage.removeItem("pf_token"); } catch (_) { /* ignore */ }
    const { data } = await api.post("/auth/google", { session_id });
    localStorage.setItem("pf_token", data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("pf_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, loginGoogle, logout, refresh }),
    [user, loading, login, signup, loginGoogle, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
